import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const QuickApplyModal = ({ job, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    proposedRate: '',
    availability: '',
    coverLetter: '',
    estimatedDuration: ''
  });

  const [errors, setErrors] = useState({});

  if (!job) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.proposedRate || formData?.proposedRate <= 0) {
      newErrors.proposedRate = 'Please enter a valid rate';
    }

    if (!formData?.availability) {
      newErrors.availability = 'Please specify your availability';
    }

    if (!formData?.coverLetter || formData?.coverLetter?.length < 50) {
      newErrors.coverLetter = 'Cover letter must be at least 50 characters';
    }

    if (!formData?.estimatedDuration) {
      newErrors.estimatedDuration = 'Please provide an estimated duration';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit({ 
        jobId: job?.id,
        proposedRate: parseFloat(formData?.proposedRate),
        availability: formData?.availability,
        coverLetter: formData?.coverLetter,
        estimatedDuration: formData?.estimatedDuration
      });
    }
  };

  const quickTemplates = [
    "I have extensive experience in this field and would love to discuss how I can help with your project.",
    "I\'m available to start immediately and can complete this work within your timeline.",
    "I\'ve successfully completed similar projects and can provide references upon request."
  ];

  const handleTemplateClick = (template) => {
    setFormData(prev => ({
      ...prev,
      coverLetter: prev?.coverLetter ? `${prev?.coverLetter}\n\n${template}` : template
    }));
  };

  return (
    <div
      className="fixed inset-0 z-modal bg-background/95 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-md shadow-warm-xl w-full max-w-[600px] mb-8 animate-in fade-in zoom-in-95 duration-250"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-border">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
              Quick Apply
            </h2>
            <p className="text-sm text-muted-foreground line-clamp-1">{job?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth flex-shrink-0"
            aria-label="Close application form"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-5">
          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Client's Budget Range</span>
              <span className="text-base font-semibold text-primary">
                ₱{job?.budgetMin?.toLocaleString()} - ₱{job?.budgetMax?.toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Consider this range when proposing your rate
            </p>
          </div>

          <Input
            label="Your Proposed Rate (₱)"
            type="number"
            placeholder="Enter your rate"
            description="Your hourly or project rate in Philippine Peso"
            value={formData?.proposedRate}
            onChange={(e) => handleChange('proposedRate', e?.target?.value)}
            error={errors?.proposedRate}
            required
          />

          <Input
            label="Availability"
            type="text"
            placeholder="e.g., Weekdays 9AM-5PM, Weekends"
            description="When can you start and work on this job?"
            value={formData?.availability}
            onChange={(e) => handleChange('availability', e?.target?.value)}
            error={errors?.availability}
            required
          />

          <Input
            label="Estimated Duration"
            type="text"
            placeholder="e.g., 2-3 days, 1 week"
            description="How long will it take to complete?"
            value={formData?.estimatedDuration}
            onChange={(e) => handleChange('estimatedDuration', e?.target?.value)}
            error={errors?.estimatedDuration}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Cover Letter <span className="text-error">*</span>
            </label>
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-2">Quick Templates:</p>
              <div className="flex flex-wrap gap-2">
                {quickTemplates?.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTemplateClick(template)}
                    className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-smooth"
                  >
                    Template {index + 1}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={formData?.coverLetter}
              onChange={(e) => handleChange('coverLetter', e?.target?.value)}
              placeholder="Explain why you're the best fit for this job..."
              rows={6}
              className={`
                w-full px-4 py-3 rounded-md border text-sm
                bg-background text-foreground
                focus:outline-none focus:ring-2 focus:ring-ring
                transition-smooth resize-none
                ${errors?.coverLetter ? 'border-error' : 'border-border'}
              `}
            />
            {errors?.coverLetter && (
              <p className="text-xs text-error mt-1">{errors?.coverLetter}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {formData?.coverLetter?.length} / 50 minimum characters
            </p>
          </div>

          <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Application Tips</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Be specific about your relevant experience</li>
                  <li>• Propose a competitive but fair rate</li>
                  <li>• Clearly state your availability</li>
                  <li>• Highlight what makes you the best choice</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              iconName="Send"
              iconPosition="left"
            >
              Submit Application
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickApplyModal;